import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { fetchData } from '../../helpers/apiUtils';

/**
 * KI-Werbebild (04.11.2026): Bild fuer Portal-Karte, Kursgespraech-Seite und bisherige DOI-Karte — NIE Warteraum.
 * Text im Bild ist woertlich aus der veroeffentlichten Copy und von einer zweiten KI gegengelesen.
 */
interface Kandidat {
  id: number;
  url: string;
  check_status: 'ok' | 'failed' | 'error';
  zone_flags: number;
  created_at: string;
  aktuell_quelle: boolean;
  text: {
    vorlage?: string;
    oben?: string | null;
    links?: string | null;
    rechts?: string | null;
    headline?: string | null;
    zeile?: string | null;
    redakteur?: string | null;
    logos?: Array<{ name: string; isin: string }>;
    hinweis?: string;
  } | null;
  pruefung: { gruende?: string[] } | null;
}

interface WerbebildDaten {
  url: string | null;
  source: 'ai' | 'manual' | 'off' | null;
  auto_enabled: boolean;
  zustand: { status: string | null; detail: string | null; last_attempt_s: string | null } | null;
  kandidaten: Kandidat[];
}

const QUELLE_TEXT: Record<string, string> = {
  ai: 'automatisch gewählt',
  manual: 'von Hand gewählt (wird nicht mehr automatisch ersetzt)',
  off: 'aus — die Karten zeigen das Videostandbild',
};

function textZeilen(k: Kandidat): string {
  const t = k.text;
  if (!t) return '';
  return [t.oben, t.links, t.rechts, t.headline, t.zeile].filter(Boolean).join(' · ');
}

/** Studio-Look (seit 04.11. abends): wer und welche Logos im Bild sind. */
function merkmale(k: Kandidat): string {
  const t = k.text;
  if (!t) return '';
  const teile = [t.redakteur ? `mit ${t.redakteur}` : null, t.logos?.length ? `Logos: ${t.logos.map((l) => l.name).join(', ')}` : null, t.hinweis ?? null];
  return teile.filter(Boolean).join(' · ');
}

/** Kartenvorschau mit den Overlays der Portal-Karte (Badge oben links, Uhrzeit unten links). */
const KartenVorschau: React.FC<{ url: string }> = ({ url }) => (
  <Box sx={{ position: 'relative', width: 360, aspectRatio: '16 / 9', borderRadius: 1, overflow: 'hidden', bgcolor: '#111' }}>
    <Box component="img" src={url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    <Box sx={{ position: 'absolute', top: 8, left: 8, bgcolor: 'rgba(0,0,0,.85)', color: '#fff', px: 1, py: 0.25, borderRadius: 1, fontSize: 11, fontWeight: 700 }}>
      ● LIVE-WEBINAR
    </Box>
    <Box sx={{ position: 'absolute', bottom: 8, left: 10, color: '#fff', fontWeight: 800, fontSize: 18, textShadow: '0 2px 4px #000' }}>
      Heute, 18:00 Uhr
    </Box>
  </Box>
);

const WerbebildPanel: React.FC<{ webinarId: string }> = ({ webinarId }) => {
  const [daten, setDaten] = useState<WerbebildDaten | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [erzeugt, setErzeugt] = useState(false);
  const poll = useRef<ReturnType<typeof setInterval> | null>(null);
  const basis = `/api/webinar-room-admin/${encodeURIComponent(webinarId)}/promo-image`;

  const laden = useCallback(async () => {
    const r = await fetchData<WerbebildDaten>(basis);
    if (r.success && r.data) {
      setDaten(r.data);
      setFehler(null);
    } else setFehler(r.error || 'Werbebild konnte nicht geladen werden');
  }, [basis]);

  useEffect(() => {
    void laden();
    return () => {
      if (poll.current) clearInterval(poll.current);
    };
  }, [laden]);

  const setze = async (body: Record<string, unknown>) => {
    setLaeuft(true);
    const r = await fetchData(basis, { method: 'PUT', body: JSON.stringify(body) });
    setLaeuft(false);
    if (!r.success) setFehler(r.error || 'Speichern fehlgeschlagen');
    await laden();
  };

  const neuErzeugen = async () => {
    setLaeuft(true);
    const r = await fetchData(`${basis}/regenerate`, { method: 'POST', body: '{}' });
    setLaeuft(false);
    if (!r.success) {
      setFehler(r.error || 'Erzeugung konnte nicht gestartet werden');
      return;
    }
    setErzeugt(true);
    let n = 0;
    if (poll.current) clearInterval(poll.current);
    poll.current = setInterval(() => {
      n += 1;
      void laden();
      // Bis 10 Min.: je Kandidat bis zu drei Bilder (mit Logos, Korrektur, Rückfall ohne Logos).
      if (n >= 40 && poll.current) {
        clearInterval(poll.current);
        poll.current = null;
        setErzeugt(false);
      }
    }, 15_000);
  };

  if (!daten) return fehler ? <Alert severity="error">{fehler}</Alert> : <Typography variant="body2">Lädt …</Typography>;

  const aktuelleKandidaten = daten.kandidaten.filter((k) => k.aktuell_quelle);
  const zeigeKandidaten = aktuelleKandidaten.length ? aktuelleKandidaten : daten.kandidaten.slice(0, 6);

  return (
    <Stack spacing={2} data-testid="werbebild">
      <Typography variant="body2" color="text.secondary">
        Bild für die Webinar-Karte auf den Portalen, die Vorschau auf der Kursgespräch-Seite und die DOI-Karte. Der
        Warteraum zeigt es nie. Text im Bild stammt wörtlich aus der veröffentlichten Werbe-Copy und wird von einer
        zweiten KI Buchstabe für Buchstabe gegengelesen.
        {!daten.auto_enabled && ' Automatik ist noch aus — „Neu erzeugen“ legt nur Kandidaten an, Live geht ein Bild per „Verwenden“.'}
      </Typography>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        {daten.url ? <KartenVorschau url={daten.url} /> : (
          <Alert severity="info" sx={{ maxWidth: 360 }}>Kein Werbebild — die Karten zeigen das Videostandbild.</Alert>
        )}
        <Stack spacing={1}>
          <Typography variant="body2">
            <b>Status:</b> {daten.url ? QUELLE_TEXT[daten.source ?? 'ai'] : daten.source === 'off' ? QUELLE_TEXT.off : 'automatisch, noch kein Bild'}
          </Typography>
          {daten.zustand?.detail && (
            <Typography variant="caption" color="text.secondary">
              Letzter Lauf {daten.zustand.last_attempt_s ?? ''}: {daten.zustand.status} — {daten.zustand.detail}
            </Typography>
          )}
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" size="small" onClick={() => void neuErzeugen()} disabled={laeuft || erzeugt}>
              {erzeugt ? 'Wird erzeugt (bis 10 Min.) …' : 'Neu erzeugen'}
            </Button>
            <Button size="small" onClick={() => void setze({ mode: 'auto' })} disabled={laeuft || daten.source === 'ai' || daten.source === null}>
              Automatisch
            </Button>
            <Button size="small" color="inherit" onClick={() => void setze({ mode: 'off' })} disabled={laeuft || daten.source === 'off'}>
              Aus
            </Button>
          </Stack>
        </Stack>
      </Stack>
      {fehler && <Alert severity="error">{fehler}</Alert>}
      {zeigeKandidaten.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Kandidaten{aktuelleKandidaten.length ? '' : ' (ältere Copy)'}
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {zeigeKandidaten.map((k) => (
              <Stack key={k.id} spacing={0.5} sx={{ width: 240 }} data-testid={`werbebild-kandidat-${k.id}`}>
                <Box component="img" src={k.url} alt="" sx={{ width: 240, aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 1, outline: k.url === daten.url ? '3px solid #2e7d32' : 'none' }} />
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Chip size="small" color={k.check_status === 'ok' ? 'success' : 'error'} label={k.check_status === 'ok' ? 'Prüfung ok' : 'durchgefallen'} />
                  {k.zone_flags > 0 && <Chip size="small" variant="outlined" label="Ecke evtl. belegt" />}
                </Stack>
                <Typography variant="caption">{textZeilen(k)}</Typography>
                {merkmale(k) && (
                  <Typography variant="caption" color="text.secondary">
                    {merkmale(k)}
                  </Typography>
                )}
                {k.check_status !== 'ok' && k.pruefung?.gruende?.length ? (
                  <Typography variant="caption" color="error">{k.pruefung.gruende.join('; ')}</Typography>
                ) : null}
                {k.check_status === 'ok' && k.aktuell_quelle && k.url !== daten.url && (
                  <Button size="small" variant="outlined" onClick={() => void setze({ candidate_id: k.id })} disabled={laeuft}>
                    Verwenden
                  </Button>
                )}
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

export default WerbebildPanel;
